import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableCell,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  TABLE_HEAD_UNCHANGED_STOCKS,
  TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA,
  Tablecell,
} from "../../../component/Methods";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveRequests } from "../../../Redux/Reducers/selectors";
import {
  getFundOrCompositeName,
  buildRebalanceSummaryData,
} from "../../../component/Methods/mainFunctions";
import ExistingFundRebalance from "../Steps/Step2/components/ExistingFundRebalance";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";
import RebalancePopup from "../Popup/RebalancePopup";
import UnchangedStocks from "../Popup/UnchangedStocks";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReviewDataSkeleton from "../Skeletons/ReviewDataSkeleton";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import { useRebalancerState } from "../../../hooks/useOptimizedState";
import {
  getRebalancerReviewOuterSx,
  getRebalancerHorizontalScrollRowSx,
  getRebalancerReviewCardSx,
  getRebalancerSummaryTableSx,
  getRebalancerSummaryHeaderRowSx,
  getRebalancerSummaryHeaderCellSx,
  getRebalancerSummaryHeaderCellLeftSx,
  getRebalancerReviewTableFillSx,
} from "../styles/rebalancerStyles";



const ReviewData = ({
  sleeve,
  setExistingNewArray = () => {},
  existingNewArray,
  setOrders = () => {},
  sleeveDataStageOne,
  last,
  setOpenCrossPopup,
  setOnceClicked,
  onceClicked,
  showNext,
  driftValue,
  rebalanceType,
  rebalancedFunds,
  rebalancedStrategies,
  deltaRebalancer,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { funds, blacklistedTickers, strategies, brokers } =
    useRebalancerState();
  const activeRequests = useSelector(selectActiveRequests);

  const [summaryData, setSummaryData] = useState([]);
  const [preferenceData, setPreferenceData] = useState({});
  const [showPreferences, setShowPreferences] = useState(false);
  const [openPopup, setOpenPopup] = useState({});
  const [showUnchangedStocks, setShowUnchangedStocks] = useState({});

  useEffect(() => {
    if (sleeve) {
      const { newSummaryData, newPreferenceData } = buildRebalanceSummaryData(
        sleeveDataStageOne,
        brokers,
        setExistingNewArray,
        setOrders,
        last
      );
      setSummaryData(newSummaryData);
      setPreferenceData(newPreferenceData);
    }
  }, [sleeve, sleeveDataStageOne]);

  const onNext = () => {
    if (onceClicked) {
      setOpenCrossPopup(true);
      return;
    }

    setOnceClicked(true);

    const rebalancerIds = Object.values(sleeveDataStageOne)?.map(
      (strategy) => strategy?.rebalancer?.rebalancerId
    );
    const socketRequest = {
      action: "create_rebalancer_orders",
      data: {
        rebalancerIds: rebalancerIds,
        rebalancerType: rebalanceType === "all" ? 1 : 0,
      },
    };
    dispatch(addActiveRequests("create_rebalancer_orders"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Creating Single Line Orders"));
    sendWebSocketMessage(JSON.stringify(socketRequest));
    setOpenCrossPopup(true);
  };

  const handleOpenPopup = (compositeKey) => {
    setOpenPopup((prev) => ({
      ...prev,
      [compositeKey]: true,
    }));
  };

  const handleClosePopup = (compositeKey) => {
    setOpenPopup((prev) => ({
      ...prev,
      [compositeKey]: false,
    }));
  };

  const handleSwitchToFullRebalance = (strategyId, fundId) => {
    const blacklistedData = rebalancedFunds?.reduce((acc, fundId) => {
      const matchedFund = blacklistedTickers?.find(
        (item) => item?.fundId === fundId
      );

      if (matchedFund && matchedFund?.blacklistedTickers) {
        const blacklistedTickersArray =
          Object.values(matchedFund?.blacklistedTickers)?.flat() || [];

        // Only process if we have actual blacklisted tickers with valid data
        const aggregatedTickers = blacklistedTickersArray
          .filter((item) => item && item.ticker) // Filter out invalid items
          .map(({ ticker, reason }) => ({
            ...ticker,
          }));

        acc[fundId] = aggregatedTickers;
      } else {
        acc[fundId] = [];
      }

      return acc;
    }, {});

    const socketRequest = {
      action: "create_rebalancer_items",
      data: {
        fundIds: [fundId],
        blacklistedTickers: blacklistedData,
        rebalancerType: rebalanceType === "all" ? 1 : 0,
        strategies: [strategyId],
        replace: true,
      },
    };
    sendWebSocketMessage(JSON.stringify(socketRequest));
    dispatch(addActiveRequests("create_rebalancer_items"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Creating Rebalancer Items"));
  };

  // Check if we're loading rebalancer data
  const isLoadingRebalancerData = activeRequests.includes(
    "create_rebalancer_items"
  );

  // Show skeleton while loading
  if (isLoadingRebalancerData) {
    return <ReviewDataSkeleton />;
  }

  return (
    <Box sx={getRebalancerReviewOuterSx(theme, showNext)}>
      {showNext ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: 2,
              padding: "16px 20px",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "20px !important",
                  color: theme.palette.text.primary,
                }}
              >
                Review Data
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPreferences}
                    onChange={(e) => setShowPreferences(e.target.checked)}
                    size="small"
                  />
                }
                label="Preferences"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontFamily: "clashGrotesk",
                    fontWeight: "normal",
                    marginLeft: "8px",
                    color: theme.palette.text.primary,
                  },
                }}
              />
            </Box>

            {Object.keys(sleeveDataStageOne)?.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={onNext}
                  sx={{ width: "150px", height: "40px" }}
                >
                  Next
                </Button>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      <Box sx={getRebalancerHorizontalScrollRowSx(showNext)}>
        {Object.keys(sleeveDataStageOne).map((compositeKey) => {
          const [strategyId, fundId] = compositeKey.split("#");
          const fundStrategyName = getFundOrCompositeName(
            compositeKey,
            strategies,
            funds
          );
          const hideDeltaData = sleeveDataStageOne[compositeKey]?.hideDeltaData;

          const unchangedStocks =
            sleeveDataStageOne[compositeKey]?.rebalancer?.unchangedStocks;
          const unchangedCount =
            unchangedStocks && typeof unchangedStocks === "object"
              ? Object.keys(unchangedStocks).length
              : 0;

          return (
            <Box
              key={compositeKey}
              sx={getRebalancerReviewCardSx({
                width:
                  rebalanceType === "specific" &&
                  !hideDeltaData &&
                  deltaRebalancer !== 0
                    ? "700px"
                    : "500px",
                showNext,
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  ...(showNext && { flexShrink: 0 }),
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "900",
                    fontSize: "18px",
                    marginBottom: 1,
                  }}
                >
                  {fundStrategyName}
                </Typography>
                <IconButton
                  onClick={() => handleOpenPopup(compositeKey)}
                  sx={{
                    height: "20px",
                    width: "20px",
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                  }}
                >
                  <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                </IconButton>
              </Box>

              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={getRebalancerSummaryTableSx(showNext)}
              >
                <TableBody>
                  <TableRow sx={getRebalancerSummaryHeaderRowSx(theme)}>
                    <TableCell
                      align="left"
                      colSpan={1}
                      sx={getRebalancerSummaryHeaderCellLeftSx(theme)}
                    >
                      Summary
                    </TableCell>
                    <Tablecell
                      align="center"
                      sx={getRebalancerSummaryHeaderCellSx(theme)}
                    >
                      Existing
                    </Tablecell>
                    <Tablecell
                      align="center"
                      sx={getRebalancerSummaryHeaderCellSx(theme)}
                    >
                      New
                    </Tablecell>
                    {rebalanceType === "specific" &&
                    hideDeltaData !== true &&
                    deltaRebalancer !== 0 ? (
                      <Tablecell
                        align="center"
                        sx={{
                          fontSize: "12px",
                          border: "none",
                          fontWeight: "bold",
                          paddingBottom: "10px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          Preview
                          <Tooltip
                            title="Calculated Portfolio if delta rebalancer is executed"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                  backgroundColor: "rgba(0, 0, 0, 0.87)",
                                  padding: "8px 12px",
                                  maxWidth: "300px",
                                  lineHeight: "1.4",
                                  color: theme.palette.text.primary,
                                },
                              },
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "16px",
                                color: "grey.500",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </Tablecell>
                    ) : null}
                  </TableRow>

                  {summaryData
                    .filter((item) => item.compositeKey === compositeKey)
                    .map((row) =>
                      row.summary.map((subRow) => (
                        <TableRow
                          key={subRow.name}
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          <TableCell
                            colSpan={1}
                            align="left"
                            sx={{
                              fontSize: "12px",
                              paddingBottom: "2px",
                              paddingLeft: "15px",
                              border: "none",
                              color: theme.palette.text.primary,
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            {subRow.name}
                          </TableCell>
                          <Tablecell
                            align="center"
                            sx={{
                              fontSize: "12px",
                              border: "none",
                              paddingBottom: "2px",
                              color: theme.palette.text.primary,
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            {subRow.existing}
                          </Tablecell>
                          <Tablecell
                            align="center"
                            sx={{
                              fontSize: "12px",
                              border: "none",
                              paddingBottom: "2px",
                              color: theme.palette.text.primary,
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            {subRow.new}
                          </Tablecell>
                          {rebalanceType === "specific" &&
                          !hideDeltaData &&
                          deltaRebalancer !== 0 ? (
                            <Tablecell
                              align="center"
                              sx={{
                                fontSize: "12px",
                                border: "none",
                                paddingBottom: "2px",
                                color: theme.palette.text.primary,
                                backgroundColor: theme.palette.background.paper,
                              }}
                            >
                              {subRow.delta}
                            </Tablecell>
                          ) : null}
                        </TableRow>
                      ))
                    )}
                </TableBody>
              </Table>

              {showPreferences && (
                <Table size="small" stickyHeader>
                  <TableBody>
                    {preferenceData[compositeKey]?.map((row) => (
                      <TableRow
                        key={row.name}
                        sx={{ backgroundColor: theme.palette.background.paper }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "12px",
                            border: "none",
                            paddingLeft: "15px",
                            paddingBottom: "2px",
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <Tablecell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            border: "none",
                            paddingBottom: "2px",
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          {row.new}
                        </Tablecell>
                        <Tablecell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            border: "none",
                            paddingBottom: "2px",
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          {row.existing}
                        </Tablecell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {rebalanceType === "specific" &&
              !hideDeltaData &&
              deltaRebalancer !== 0 ? (() => {
                const rebalancer = sleeveDataStageOne[compositeKey]?.rebalancer;
                const existingLong = rebalancer?.existingTotalLong || 0;
                const existingShort = rebalancer?.existingTotalShort || 0;
                const deltaLong = rebalancer?.deltaTotalLong || 0;
                const deltaShort = rebalancer?.deltaTotalShort || 0;

                const longDifference = Math.abs(existingLong - deltaLong);
                const shortDifference = Math.abs(existingShort - deltaShort);
                const longExceeded = longDifference > driftValue;
                const shortExceeded = shortDifference > driftValue;
                const driftExceeded = longExceeded || shortExceeded;

                const getDriftMessage = () => {
                  if (!driftExceeded) {
                    return `Drift Value: ${driftValue}%`;
                  }

                  const exceededPositions = [];
                  if (longExceeded) exceededPositions.push("Long");
                  if (shortExceeded) exceededPositions.push("Short");

                  return `Drift Exceeded Allowed Threshold (${driftValue}%) - ${exceededPositions.join(" & ")}`;
                };

                return (
                  <Box
                    sx={{
                      backgroundColor: driftExceeded ? "#FFEBEE" : "#FFF3CD",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                      position: "relative",
                      minHeight: 80,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                        <WarningAmberIcon
                          htmlColor={driftExceeded ? "#F44336" : "#FFC107"}
                          style={{ fontSize: 16, marginRight: 4, position: "relative", top: -1 }}
                        />
                        <Typography
                          sx={{ fontWeight: 600, fontSize: 15, color: "#000000" }}
                        >
                          {getDriftMessage()}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          textDecoration: "underline",
                          color: "#1976d2",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setShowUnchangedStocks((prev) => ({
                            ...prev,
                            [compositeKey]: true,
                          }))
                        }
                      >
                        {`Unchanged Positions (${unchangedCount})`}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          handleSwitchToFullRebalance(strategyId, fundId)
                        }
                        sx={{
                          backgroundColor: "#FF9800 !important",
                          color: "#FFFFFF !important",
                          "&:hover": {
                            backgroundColor: "#F57C00 !important",
                          },
                        }}
                      >
                        Switch to Full Rebalance
                      </Button>
                    </Box>
                  </Box>
                );
              })() : null}
              {existingNewArray  ? (
                <Box sx={getRebalancerReviewTableFillSx(showNext)}>
                  <ExistingFundRebalance
                    existingNewArray={existingNewArray[compositeKey]}
                    hideDeltaData={hideDeltaData}
                    deltaRebalancer={deltaRebalancer}
                    rebalanceType={rebalanceType}
                    fillHeight={showNext}
                  />
                </Box>
              ) : (
                <p>No data available</p>
              )}

              <RebalancePopup
                open={openPopup[compositeKey] || false}
                onClose={() => handleClosePopup(compositeKey)}
                fundStrategyName={fundStrategyName}
                rebalanceType={rebalanceType}
                hideDeltaData={hideDeltaData}
                deltaRebalancer={deltaRebalancer}
                summaryData={summaryData}
                compositeKey={compositeKey}
                fundId={fundId}
                strategyId={strategyId}
                existingNewArray={existingNewArray}
                TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA={
                  TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA
                }
              />
              <UnchangedStocks
                showUnchangedStocks={showUnchangedStocks[compositeKey] || false}
                setShowUnchangedStocks={(value) =>
                  setShowUnchangedStocks((prev) => ({
                    ...prev,
                    [compositeKey]: value,
                  }))
                }
                stock={
                  sleeveDataStageOne[compositeKey]?.rebalancer?.unchangedStocks
                }
                tableHeader={TABLE_HEAD_UNCHANGED_STOCKS}
              />
            </Box>
          );
        })}
        {showNext ? <Box sx={{ flexShrink: 0, width: 24 }} /> : null}
      </Box>
    </Box>
  );
};

export default ReviewData;
